import { useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useUiStore } from "../store/ui";
import { useAuthStore } from "../store/auth";
import { useLangStore } from "../store/lang";
import { t } from "../i18n/strings";
import { LanguagePicker } from "./LanguagePicker";
import { api } from "../lib/api";

// Full-height slide-in drawer for ≤768px. Holds the complete nav (primary +
// secondary), language picker, user chip, sign-out. Visible only via CSS at
// the mobile breakpoint; the AppShell renders it always but it's
// display:none on desktop.
const PRIMARY: Array<{ to: string; key: string; label?: string }> = [
  { to: "/dashboard", key: "nav.dashboard" },
  { to: "/inbox", key: "nav.inbox", label: "AI Inbox" },
  { to: "/invoices", key: "nav.invoices" },
  { to: "/bills", key: "nav.bills" },
  { to: "/bank", key: "nav.bank" },
  { to: "/payroll", key: "nav.payroll" },
  { to: "/reports", key: "nav.reports" },
];

const SECONDARY: Array<{ to: string; label: string }> = [
  { to: "/customers", label: "Customers" },
  { to: "/suppliers", label: "Suppliers" },
  { to: "/employees", label: "Employees" },
  { to: "/team", label: "Team" },
  { to: "/spec", label: "Spec" },
  { to: "/settings", label: "Settings" },
];

export function MobileDrawer() {
  const { drawerOpen, closeDrawer } = useUiStore();
  const { user, refreshToken, clear } = useAuthStore();
  const { lang } = useLangStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Close on route change.
  useEffect(() => {
    closeDrawer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Close on Escape.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeDrawer();
    };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [drawerOpen, closeDrawer]);

  async function signOut() {
    if (refreshToken) {
      await api.post("/auth/logout", { refresh_token: refreshToken }).catch(() => {});
    }
    clear();
    navigate("/login", { replace: true });
  }

  const initials = (user?.name ?? "?")
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <>
      <div
        className={"drawer-backdrop" + (drawerOpen ? " open" : "")}
        onClick={closeDrawer}
        aria-hidden="true"
      />
      <aside
        className={"mobile-drawer" + (drawerOpen ? " open" : "")}
        aria-hidden={!drawerOpen}
      >
        <div className="drawer-header">
          <div className="brand">
            <div className="brand-mark">C</div>
            ClearBooks
          </div>
          <button
            type="button"
            className="drawer-close"
            onClick={closeDrawer}
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>

        <nav className="drawer-nav">
          {PRIMARY.map((item) => {
            const translated = t(item.key, lang);
            const label = translated && translated !== item.key ? translated : (item.label ?? item.key);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => "drawer-link" + (isActive ? " active" : "")}
              >
                {label}
              </NavLink>
            );
          })}
          <div className="drawer-divider" />
          {SECONDARY.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => "drawer-link secondary" + (isActive ? " active" : "")}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="drawer-footer">
          <div className="drawer-user">
            <div className="avatar av-1">{initials}</div>
            <span>{user?.name ?? ""}</span>
          </div>
          <LanguagePicker />
          <button type="button" className="btn btn-line" style={{ width: "100%", justifyContent: "center" }} onClick={signOut}>
            {t("auth.signOut", lang)}
          </button>
        </div>
      </aside>
    </>
  );
}
